import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="photo-sort"
// Native HTML5 drag-and-drop reordering for photo grids.
// Sends the moved photo's ID and new position to the server.
//
// Usage:
//   <div data-controller="photo-sort" data-photo-sort-url-value="/deals/1/deal_photos">
//     <div data-photo-sort-target="item" data-photo-id="1" draggable="true">...</div>
//     <div data-photo-sort-target="item" data-photo-id="2" draggable="true">...</div>
//   </div>
export default class extends Controller {
  static targets = ["item"]
  static values = { url: String }

  initialize() {
    this.dragItem = null
    this.lastMoveTime = 0
    this.boundDragStart = this.dragStart.bind(this)
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)
  }

  itemTargetConnected(item) {
    item.addEventListener("dragstart", this.boundDragStart)
    item.addEventListener("dragover", this.boundDragOver)
    item.addEventListener("dragend", this.boundDragEnd)
  }

  itemTargetDisconnected(item) {
    item.removeEventListener("dragstart", this.boundDragStart)
    item.removeEventListener("dragover", this.boundDragOver)
    item.removeEventListener("dragend", this.boundDragEnd)
  }

  dragStart(event) {
    if (event.target.closest("a, button, form")) return

    this.dragItem = event.currentTarget
    event.currentTarget.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const now = Date.now()
    if (now - this.lastMoveTime < 50) return
    this.lastMoveTime = now

    const target = event.currentTarget
    if (target === this.dragItem || !target.dataset.photoId) return

    const rect = target.getBoundingClientRect()
    const midX = rect.left + rect.width / 2

    if (event.clientX < midX) {
      target.parentNode.insertBefore(this.dragItem, target)
    } else {
      target.parentNode.insertBefore(this.dragItem, target.nextSibling)
    }
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    const movedItem = this.dragItem
    this.dragItem = null
    if (movedItem) this.savePosition(movedItem)
  }

  async savePosition(item) {
    const photoId = item.dataset.photoId
    const position = this.itemTargets.indexOf(item) + 1
    const url = `${this.urlValue}/${photoId}/reorder`

    const meta = document.querySelector("meta[name='csrf-token']")
    const token = meta?.content || ""

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({ position })
      })

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`)
      }
    } catch {
      document.dispatchEvent(new CustomEvent("toast:show", {
        detail: {
          type: "error",
          message: "Failed to save photo order",
          description: "Please try again."
        }
      }))
    }
  }
}
